import * as React from 'react';

type MapContext = {
  map: google.maps.Map | undefined;
  setMap: React.Dispatch<React.SetStateAction<google.maps.Map | undefined>>;
  goToCoordinates: (northEast: Point, southWest: Point) => void;
};

type MapProviderProps = {
  children: React.ReactNode;
};

type Point = {
  lat: number;
  lng: number;
};

const MapContext = React.createContext<MapContext>(undefined!);

export const MapProvider: React.FC<MapProviderProps> = ({ children }) => {
  const [map, setMap] = React.useState<google.maps.Map>();
  const [mapBounds, setMapBounds] = React.useState<
    google.maps.LatLngBounds | undefined
  >(undefined);

  React.useEffect(() => {
    if (map && mapBounds) {
      map.fitBounds(mapBounds);
      // Google maps doesn't specify how much padding this adds
      // It merely creates a viewport "to contain the given bounds"
      // We may want to repeatedly zoom in from here until
      // we exclude a bound and then zoom back out

      var queryParams = new URLSearchParams(window.location.search);
      queryParams.delete('placeId');
      history.replaceState(null, '', '?' + queryParams.toString());
    }
  }, [map, mapBounds]);

  const goToCoordinates = (northEast: Point, southWest: Point) => {
    const { lat: northEastLat, lng: northEastLng } = northEast;
    const { lat: southWestLat, lng: southWestLng } = southWest;
    if (!(northEastLat > southWestLat && northEastLng > southWestLng)) {
      throw new Error('invalid bounds');
    }
    const ne = new google.maps.LatLng(northEastLat, northEastLng);
    const sw = new google.maps.LatLng(southWestLat, southWestLng);
    const bounds = new google.maps.LatLngBounds(sw, ne);
    setMapBounds(bounds);
  };

  const contextValue: MapContext = {
    map,
    setMap,
    goToCoordinates,
  };
  return (
    <MapContext.Provider value={contextValue}>{children}</MapContext.Provider>
  );
};

export const useMap = () => {
  const context = React.useContext(MapContext);

  if (context === undefined) {
    throw new Error('useMap must be used within a MapProvider');
  }

  return context;
};
